<template>
<div id="app" :class="[class_name, {'is-mobile-device': is_mobile}]">
    <div v-if="not_supported" class="ie-go-home has-text-centered">
        <p class="subtitle is-4"><router-link :to="{ name: 'Homepage' }"><img width="120" src="@/assets/logo.png" /></router-link></p>
        <h1 class="title is-4">Your browser is not supported</h1>
        <div class="content">
            <p class="subtitle is-5">Please use one of the recommended browsers below:</p>
            <p>
                <a target="_blank" href="https://www.google.com/chrome/">Chrome</a> -
                <a target="_blank" href="https://www.mozilla.org/en-US/firefox/new/">Firefox</a> -
                <a target="_blank" href="https://www.microsoft.com/en-nz/windows/microsoft-edge">Edge</a> -
                <a target="_blank" href="https://support.apple.com/downloads/safari">Safari</a> -
                <a target="_blank" href="https://www.opera.com/download">Opera</a>
            </p>
        </div>
    </div>
    <template v-else-if="site_data">
        <Header :site_data="site_data" />
        <router-view :site_data="site_data" />
        <Footer :site_data="site_data" />
        <template v-if="editlink">
            <a id="btn-edit-link" :href="editlink" target="_blank">Edit</a>
        </template>
        <PolicyOverlay :message="site_data.popup_policies" />
        <MessageModal />
    </template>
</div>
</template>

<script>
import Header from './components/blocks/Header';
import Footer from './components/blocks/Footer';
import MessageModal from './components/blocks/MessageModal';
import PolicyOverlay from './components/blocks/PolicyOverlay';
import slugify from 'slugify';
import './css/styles.css';

export default {
    name: 'App',
    metaInfo() {
        return {
            // Children can override the title.
            title: this.pagetitle,
            // Result: My Page Title ← My Site
            // If a child changes the title to "My Other Page Title",
            // it will become: My Other Page Title ← My Site
            titleTemplate: (titleChunk) => {
                return titleChunk ? `${titleChunk}` : document.title;
            },
            // Define meta tags here.
            htmlAttrs: {
                lang: 'en'
            },
            link: this.meta_links,
            meta: this.meta_items
        }
    },
    data() {
        return {
            pagetitle   :   null,
            site_data   :   window.appInitialData
        }
    },
    components: {
        Header,
        Footer,
        MessageModal,
        PolicyOverlay
    },
    computed: {
        editlink() {
            if (!this.site_data) {
                return null;
            }

            return this.site_data.edit_link;
        },
        class_name() {
            return slugify(this.$route.name, {lower: true});
        },
        not_supported() {
            if (!window.localStorage) return true;

            let ua      =   window.navigator.userAgent,
                msie    =   ua.indexOf('MSIE '),
                trident =   ua.indexOf('Trident/'); //IE 11

            return msie > 0 || trident > 0;
        },
        meta_links() {
            let links   =   [];
            if (this.site_data && this.site_data.meta && this.site_data.meta.canonical) {
                links.push({rel: 'canonical', href: this.site_data.meta.canonical});
            }
            return links;
        },
        meta_items() {
            let meta    =   [
                {'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8'},
                {name: 'viewport', content: 'width=device-width, initial-scale=1.0'}
            ];
            if (this.site_data && this.site_data.meta) {
                if (this.site_data.meta.description) {
                    meta.push({
                        name    :   'description',
                        content :   this.site_data.meta.description
                    });
                }
                if (this.site_data.meta.keywords) {
                    meta.push({
                        name    :   'keywords',
                        content :   this.site_data.meta.keywords
                    });
                }
                if (this.site_data.meta.robots) {
                    meta.push({
                        name    :   'robots',
                        content :   this.site_data.meta.robots
                    });
                }
                if (this.site_data.meta.social) {
                    this.site_data.meta.social.forEach((o) => {
                        if (o.content) {
                            meta.push(o);
                        }
                    });
                }
            }

            return meta;
        },
        user_agent()
        {
            return navigator.userAgent;
        },
        is_mobile()
        {
            let a   =   navigator.userAgent||navigator.vendor||window.opera;

            if (a.indexOf('Macintosh') > -1) {
                try {
                    document.createEvent("TouchEvent");
                    return true;
                } catch (e) {}
            }

            return (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od|ad)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4)));
        },
    },
    watch: {
        $route(to, from) {
            if (to.path == from.path) {
                return
            }

            this.site_data  =   null;

            if (to.path.indexOf("/contact") == 0 && location.hostname != "localhost") {
                localStorage.contactRefPage = location.protocol + '//'+ location.host + from.path
            } else {
                localStorage.removeItem("contactRefPage")
            }

            this.get_page_data(to.fullPath);
            // this.$bus.$emit('onPageChange');
            // NProgress.start();
        }
    },
    created() {
        localStorage.removeItem("contactRefPage")
        let me  =   this;
        $(window).on('scroll', function(e) {
            me.$bus.$emit('onWindowScroll', $(window).scrollTop());
        }).on('resize', function(e) {
            me.$bus.$emit('onWindowSize', $(window).width());
        });

        if (!this.site_data) {
            this.get_page_data(this.$route.fullPath);
        } else {
            if (this.site_data.redirect) {
                if (this.site_data.is_external) {
                    window.location.href    =   this.site_data.redirect;
                } else {
                    this.$router.push(this.site_data.redirect);
                }

                return false;
            }

            csrf            =   this.site_data.session.csrf;
            this.pagetitle  =   this.site_data.title;
            this.concat_block();
        }
    },
    methods :   {
        get_page_data(path, seamless) {
            if (this.not_supported) return false;

            let me  =   this;
            axios.get(
                base_url + me.get_endpath(path)
            ).then((resp) => {
                me.handle_page_data(resp);
            }).catch((error) => {
                // if (error && error.response && error.response.status) {
                //     me.$router.push('/' + error.response.status);
                // }
            });
        },
        get_endpath(path) {
            path    =   path.ltrim('/');
            return path;
        },
        handle_page_data(resp) {
            if (!resp.data) {
                return this.$router.replace('/403');
            }

            if (resp.data.redirect) {
                if (resp.data.is_external) {
                    window.location.href    =   resp.data.redirect;
                } else {
                    this.$router.push(resp.data.redirect);
                }

                return false;
            }

            csrf            =   resp.data.session.csrf;
            this.pagetitle  =   resp.data.title;
            this.site_data  =   resp.data;
            this.concat_block();
        },
        concat_block() {
            if (!this.site_data.block_count) return false;
            if (this.site_data.blocks.length < this.site_data.block_count) {
                const stagingParam = this.$route.query && this.$route.query.stage ? `stage=${this.$route.query.stage}&` : ''
                axios.get(
                    base_url + this.get_endpath(this.$route.path) + `?${stagingParam}block_only=1&bc=` + this.site_data.blocks.length
                ).then((resp) => {
                    this.site_data.blocks.push(resp.data);
                    this.concat_block();
                }).catch((error) => {
                    // if (error && error.response && error.response.status) {
                    //     me.$router.push('/' + error.response.status);
                    // }
                });
            } else {
                this.$nextTick().then(() => {
                    if (location.hash && $(location.hash).length) {
                        $(window).scrollTop($(location.hash).offset().top - $('#header').height() - 64);
                    }
                });
            }
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/App.vue